import { translationKey } from '../../i18n';
import { localize, readAppLanguage } from '../../i18n';
import useBackHandler from '../../hooks/useBackHandler';
import SingleLineText from '../../components/ui/SingleLineText';
import Button from '../../components/ui/FitButton';
import React, { useRef, useState } from 'react';
import { isValidDosage } from './reviewDraftError';
import { Box, Typography, IconButton, CircularProgress, Popover } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';



import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import BottomSheet from '../../components/BottomSheet';
import EditTimesSheet from '../../components/EditTimesSheet';
import EditAlertMethodSheet from '../../components/EditAlertMethodSheet';
import EditSnoozeSheet from '../../components/EditSnoozeSheet';
import MedImage from '../../components/MedImage';
import MedicationFrequencyEditor from './MedicationFrequencyEditor';
import MedicationNameEditSheet from './MedicationNameEditSheet';
import MedicationPeriodEditor, { addDaysKey } from './MedicationPeriodEditor';
import MedicationPhotoSheet, { hiddenPhotoInputStyle } from './MedicationPhotoSheet';
import {
    MEDICATION_CATEGORY_OPTIONS,
    MEDICATION_DOSAGE_METHOD_OPTIONS,
    MEDICATION_INTAKE_FORM_OPTIONS,
    MedicationDosagePerTimeEditor,
    MedicationMemoEditor,
    MedicationOptionEditor,
    medicationIntakeMeta,
} from './MedicationDetailEditors';

const FREQ_LABEL = {
    DAILY: translationKey('매일'), EVERY_OTHER_DAY: translationKey('격일'), SPECIFIC_DAYS: translationKey('특정 요일'),
    HOURLY_INTERVAL: translationKey('몇 시간마다'), EVERY_N_DAYS: translationKey('며칠마다'), CYCLE: translationKey('먹고 쉬고 반복'), MONTHLY: translationKey('매월 특정일'),
    AS_NEEDED: translationKey('필요할 때만'),
};
const DAY_LABEL = {
    MON: translationKey('월'), TUE: translationKey('화'), WED: translationKey('수'), THU: translationKey('목'), FRI: translationKey('금'), SAT: translationKey('토'), SUN: translationKey('일'),
};
const DAY_ORDER = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const CATEGORY_OPTIONS = MEDICATION_CATEGORY_OPTIONS;
const INTAKE_FORM_OPTIONS = MEDICATION_INTAKE_FORM_OPTIONS;
const METHOD_OPTIONS = MEDICATION_DOSAGE_METHOD_OPTIONS;

function intakeMeta(value) {
    return medicationIntakeMeta(value);
}

function freqDetail(form) {
    switch (form.frequencyType) {
        case 'SPECIFIC_DAYS': {
            const labels = (form.specificDays || [])
                .map(day => day.trim())
                .filter(Boolean)
                .sort((a, b) => {
                    const ai = DAY_ORDER.indexOf(a);
                    const bi = DAY_ORDER.indexOf(b);
                    return (ai === -1 ? DAY_ORDER.length : ai) - (bi === -1 ? DAY_ORDER.length : bi);
                })
                .map(day => localize(DAY_LABEL[day] || day));
            return `${localize(FREQ_LABEL.SPECIFIC_DAYS)} (${labels.join(', ')})`;
        }
        case 'HOURLY_INTERVAL': return localize('{v0}시간마다', { v0: form.hourlyInterval });
        case 'EVERY_N_DAYS': return localize('{v0}일마다', { v0: form.intervalDays });
        case 'CYCLE': return localize('{v0}일 복용 / {v1}일 휴약', { v0: form.cycleDaysOn, v1: form.cycleDaysOff });
        case 'MONTHLY': return localize('매월 {v0}일', { v0: (form.monthlyDays || []).join(', ') });
        default: return localize(FREQ_LABEL[form.frequencyType] || form.frequencyType);
    }
}

function getTimePeriod(timeStr) {
    if (!timeStr) return '';
    const h = parseInt(timeStr.split(':')[0], 10);
    if (h < 6) return translationKey('새벽');
    if (h < 11) return translationKey('아침');
    if (h < 15) return translationKey('점심');
    if (h < 17) return translationKey('오후');
    if (h < 21) return translationKey('저녁');
    return translationKey('밤');
}

function timesText(times) {
    if (!times || times.length === 0) return localize('시간 미설정');
    const language = readAppLanguage();
    if (language !== 'ko') {
        const formatter = new Intl.DateTimeFormat(language === 'ja' ? 'ja-JP' : 'en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
        return times.map(time => {
            const [hour, minute] = time.split(':').map(Number);
            return formatter.format(new Date(Date.UTC(2020, 0, 1, hour, minute)));
        }).join(', ');
    }
    return times.map((t) => {
        const [h, m] = t.split(':').map((n) => parseInt(n, 10));
        const period = localize(getTimePeriod(t));
        return m === 0 ? localize("{v0} {v1}시", { v0: period, v1: h }) : localize("{v0} {v1}시 {v2}분", { v0: period, v1: h, v2: m });
    }).join(', ');
}

function durationText(min) {
    if (!min || min <= 0) return '-';
    const h = Math.floor(min / 60);
    const m = min % 60;
    if (h === 0) return localize('{v0}분', { v0: m });
    if (m === 0) return localize('{v0}시간', { v0: h });
    return localize('{v0}시간 {v1}분', { v0: h, v1: m });
}

/* ─────────── 작은 빌딩 블록 ─────────── */

function SheetSaveFooter({ onClick, disabled = false }) {
    return (
        <>
            <Button fullWidth onMouseDown={(event) => event.preventDefault()} onClick={onClick} disabled={disabled} sx={{
                bgcolor: '#ffe566', color: '#5a4a00', borderRadius: 3, py: 1.45,
                fontSize: '1.05rem', fontWeight: 700, boxShadow: 'none',
                '&:hover': { bgcolor: '#e8d040', boxShadow: 'none' },
            }}>{localize("저장")}</Button>
        </>
    );
}




/** 알림 카드의 행 — 작은 라벨 + 큰 값 + 우측 ✏ */

/* ─────────── 메인 ─────────── */

export default function StepConfirm({
    form, setForm, goToStep = () => {}, isEdit, imagePreview,
    onPatchField,           // (patch) => 등록 모드: setForm만 / 수정 모드: setForm + PATCH
    smartSource,            // 'api' | 'ai' | null — AI 출처 popover에 사용
    existingMedications = [],
    fileInputRef,
    handleImageSelect,
    imageSaving = false,
    onPickCamera,
    onPickLibrary,
    directPhotoInput = false,
    clearImage,
}) {
    const isAsNeeded = form.frequencyType === 'AS_NEEDED';
    const currentIntakeMeta = intakeMeta(form.intakeForm);
    const [aiPopoverEl, setAiPopoverEl] = useState(null);
    useBackHandler(!!aiPopoverEl, () => setAiPopoverEl(null), 1300);
    const [openSheet, setOpenSheet] = useState(null);     // 'time' | 'alert' | 'snooze' | null
    const [memoDraft, setMemoDraft] = useState('');
    const [frequencyDraft, setFrequencyDraft] = useState(null);
    const [periodDraft, setPeriodDraft] = useState(null);
    const [detailDraft, setDetailDraft] = useState(null);
    const [photoSheetOpen, setPhotoSheetOpen] = useState(false);
    const cameraFileInputRef = useRef(null);

    const patch = (p) => {
        if (onPatchField) onPatchField(p);
        else setForm(prev => ({ ...prev, ...p }));
    };
    const openDetailSheet = (section) => {
        setDetailDraft({
            category: form.category || 'OTHER',
            intakeForm: form.intakeForm || 'ORAL',
            dosageMethod: form.dosageMethod || 'ANYTIME',
            dosagePerTime: form.dosagePerTime ?? 1,
        });
        setOpenSheet(section);
    };
    const openFrequencySheet = () => {
        setFrequencyDraft({
            frequencyType: form.frequencyType || 'DAILY',
            specificDays: [...(form.specificDays || [])],
            hourlyInterval: form.hourlyInterval || '',
            intervalDays: form.intervalDays || '',
            cycleDaysOn: form.cycleDaysOn || '',
            cycleDaysOff: form.cycleDaysOff || '',
            monthlyDays: [...(form.monthlyDays || [])],
            times: [...(form.times || [])],
        });
        setOpenSheet('frequency');
    };
    const openPeriodSheet = () => {
        setPeriodDraft({
            startDate: form.startDate,
            indefinite: form.indefinite,
            durationDays: form.durationDays,
            endDate: form.endDate,
        });
        setOpenSheet('period');
    };
    const openMemoSheet = () => {
        setMemoDraft(form.memo || '');
        setOpenSheet('memo');
    };
    const commitFrequencyDraft = () => {
        if (!frequencyDraft) {
            setOpenSheet(null);
            return;
        }
        patch(frequencyDraft);
        setOpenSheet(null);
    };
    const commitPeriodDraft = () => {
        if (!periodDraft) {
            setOpenSheet(null);
            return;
        }
        const nextPeriod = { ...periodDraft };
        if (!nextPeriod.indefinite && !nextPeriod.endDate) {
            const days = Number(nextPeriod.durationDays) > 0 ? Number(nextPeriod.durationDays) : 3;
            nextPeriod.endDate = addDaysKey(nextPeriod.startDate || form.startDate, days - 1);
        }
        patch(nextPeriod);
        setOpenSheet(null);
    };
    const detailSheetSections = ['category', 'intakeForm', 'method', 'dosage'];
    const detailDraftIntakeMeta = intakeMeta(detailDraft?.intakeForm || form.intakeForm);
    const detailSheetTitleMap = {
        category: translationKey('종류 수정'),
        intakeForm: translationKey('사용 방법 수정'),
        method: localize("{v0} 수정", { v0: localize(detailDraftIntakeMeta.methodLabel) }),
        dosage: localize("{v0} 수정", { v0: localize(detailDraftIntakeMeta.doseLabel) }),
    };
    const renderDetailSheetContent = () => {
        if (!detailDraft) return null;
        if (openSheet === 'category') {
            return (
                <MedicationOptionEditor
                    options={CATEGORY_OPTIONS}
                    value={detailDraft.category || 'OTHER'}
                    onChange={(value) => setDetailDraft(prev => ({ ...prev, category: value }))}
                />
            );
        }
        if (openSheet === 'intakeForm') {
            return (
                <MedicationOptionEditor
                    options={INTAKE_FORM_OPTIONS}
                    value={detailDraft.intakeForm || 'ORAL'}
                    onChange={(value) => setDetailDraft(prev => ({ ...prev, intakeForm: value }))}
                />
            );
        }
        if (openSheet === 'method') {
            return (
                <MedicationOptionEditor
                    options={METHOD_OPTIONS}
                    value={detailDraft.dosageMethod || 'ANYTIME'}
                    onChange={(value) => setDetailDraft(prev => ({ ...prev, dosageMethod: value }))}
                />
            );
        }
        if (openSheet === 'dosage') {
            return (
                <MedicationDosagePerTimeEditor
                    label={localize(detailDraftIntakeMeta.doseLabel)}
                    unit={detailDraftIntakeMeta.unit}
                    value={detailDraft.dosagePerTime ?? 1}
                    onChange={(value) => setDetailDraft(prev => ({ ...prev, dosagePerTime: value }))}
                />
            );
        }
        return null;
    };
    const commitDetailDraft = () => {
        if (!detailDraft) {
            setOpenSheet(null);
            return;
        }
        if (!isValidDosage(detailDraft.dosagePerTime)) return;
        patch({
            category: detailDraft.category || 'OTHER',
            intakeForm: detailDraft.intakeForm || 'ORAL',
            dosageMethod: detailDraft.dosageMethod || 'ANYTIME',
            dosagePerTime: Number(detailDraft.dosagePerTime),
        });
        setOpenSheet(null);
        setDetailDraft(null);
    };
    const closeDetailSheet = () => {
        setOpenSheet(null);
        setDetailDraft(null);
    };
    const commitMemoDraft = () => {
        patch({ memo: memoDraft || '' });
        setOpenSheet(null);
    };
    const handlePhotoInputChange = (event) => {
        handleImageSelect?.(event);
        setPhotoSheetOpen(false);
        const timeout = typeof window !== 'undefined' ? window.setTimeout : setTimeout;
        timeout(() => {
            if (event.target) event.target.value = '';
        }, 0);
    };

    const alertMethodText = form.alertType === 'FULLSCREEN'
        ? localize('벨소리 알림 ({v0}%)', { v0: form.alarmVolume })
        : form.alertType === 'VIBRATION'
            ? localize('진동 알림')
            : localize('일반 알림');
    const compactTimes = (form.times || []).filter(Boolean);
    const compactPeriodText = isAsNeeded
        ? localize(translationKey('필요할 때만'))
        : form.indefinite
            ? localize(translationKey('매일'))
            : form.endDate
                ? localize('{date}까지', { date: form.endDate })
                : localize('기간 미설정');
    const reminderText = form.reminderEnabled
        ? localize('{v0}분마다, 최대 {v1}', { v0: form.reminderIntervalMinutes, v1: durationText(form.maxReminderMinutes) })
        : localize('집요한 알림 꺼짐');
    const detailCardSx = {
        bgcolor: '#fff',
        borderRadius: 2.5,
        p: 1.45,
        border: '1px solid #eef2f7',
        mb: 1,
    };
    const editButtonSx = {
        width: 48,
        height: 48,
        color: '#9ca3af',
        flexShrink: 0,
        borderRadius: 2.2,
        bgcolor: '#f8fafc',
        border: '1px solid #eef2f7',
        '&:hover': { bgcolor: '#f1f5f9' },
        '&:active': { bgcolor: '#e5e7eb' },
    };
    const renderSummaryRow = (label, value) => (
        <Box
            key={localize(label)}
            sx={{
                display: 'grid',
                gridTemplateColumns: '38px 1fr',
                gap: 0.75,
                alignItems: 'baseline',
            }}
        >
                <SingleLineText sx={{ fontSize: '0.8rem', fontWeight: 650, color: '#9ca3af', lineHeight: 1.35 }}>
                {localize(label)}
            </SingleLineText>
            {label === translationKey('알림') && compactTimes.length >= 3 && !isAsNeeded ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.45, pt: 0.05 }}>
                    {compactTimes.map(time => (
                        <Box
                            key={time}
                            component="span"
                            sx={{
                                px: 0.65,
                                py: 0.28,
                                borderRadius: '999px',
                                bgcolor: '#fffbeb',
                                border: '1px solid #fde68a',
                                color: '#8a7000',
                                fontSize: '0.8rem',
                                fontWeight: 650,
                                lineHeight: 1.2,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {timesText([time])}
                        </Box>
                    ))}
                </Box>
            ) : (
                <Typography sx={{ fontSize: '0.94rem', fontWeight: 650, color: label === translationKey('알림') ? '#8a7000' : '#374151', lineHeight: 1.35, wordBreak: 'keep-all' }}>
                    {value}
                </Typography>
            )}
        </Box>
    );
    const renderDetailRow = ({ label, value, onEdit, expanded, onToggle, children, times = [] }) => {
        const clickable = onEdit || onToggle;
        const handleClick = onEdit || onToggle;
        return (
            <Box key={localize(label)} sx={{ borderTop: '1px solid #f1f5f9' }}>
                <Box
                    role={clickable ? 'button' : undefined}
                    tabIndex={clickable ? 0 : undefined}
                    onClick={handleClick}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        py: 1.25,
                        minHeight: 62,
                        cursor: clickable ? 'pointer' : 'default',
                        borderRadius: 2,
                        '&:active': clickable ? { bgcolor: '#f8fafc' } : undefined,
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <SingleLineText sx={{ fontSize: '0.78rem', color: '#999', fontWeight: 650, mb: 0.25 }}>
                            {localize(label)}
                        </SingleLineText>
                        {!expanded && (
                            <Typography sx={{ fontSize: '0.98rem', color: '#1a1a1a', fontWeight: 650, lineHeight: 1.35, wordBreak: 'keep-all' }}>
                                {value}
                            </Typography>
                        )}
                        {times.length > 0 && !expanded && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.55, mt: 0.75 }}>
                                {times.map(time => (
                                    <Box
                                        key={time}
                                        component="span"
                                        sx={{
                                            px: 0.85,
                                            py: 0.36,
                                            borderRadius: '999px',
                                            bgcolor: '#f8fafc',
                                            border: '1px solid #e5e7eb',
                                            color: '#475569',
                                            fontSize: '0.82rem',
                                            fontWeight: 650,
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {timesText([time])}
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                    {clickable && (
                        <IconButton
                            aria-label={localize("{v0} 수정", { v0: localize(label) })}
                            onClick={(event) => {
                                event.stopPropagation();
                                handleClick();
                            }}
                            sx={{
                                ...editButtonSx,
                                color: expanded ? '#5a4a00' : editButtonSx.color,
                                bgcolor: expanded ? '#fffbe6' : editButtonSx.bgcolor,
                            }}
                        >
                            {expanded ? <CheckIcon sx={{ fontSize: 20 }} /> : <EditOutlinedIcon sx={{ fontSize: 20 }} />}
                        </IconButton>
                    )}
                </Box>
                {expanded && (
                    <Box sx={{ pb: 1.2 }}>
                        {children}
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.85,
                    p: 1,
                    mb: 1.15,
                    borderRadius: 3,
                    bgcolor: '#fff',
                    border: isEdit ? '1px solid #eef2f7' : '1.5px solid #e5e7eb',
                    boxShadow: isEdit ? 'none' : '0 2px 10px rgba(15, 23, 42, 0.05)',
                }}
            >
                <Box
                    sx={{
                        bgcolor: '#fff',
                        borderRadius: 2.5,
                        p: 1.25,
                        border: isEdit ? 'none' : '1px solid #eef2f7',
                        display: 'flex',
	                        alignItems: isEdit ? 'center' : 'flex-start',
                        gap: 1.2,
                    }}
                >
                    {fileInputRef && (
                        <>
                            <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                ref={cameraFileInputRef}
                                style={hiddenPhotoInputStyle}
                                onChange={handlePhotoInputChange}
                            />
                            <input
                                type="file"
                                accept="image/*"
                                ref={fileInputRef}
                                style={hiddenPhotoInputStyle}
                                onChange={handlePhotoInputChange}
                            />
                        </>
                    )}
                    <Box
                        role={fileInputRef ? 'button' : undefined}
                        tabIndex={fileInputRef ? 0 : undefined}
	                        onClick={() => {
	                            if (!fileInputRef) return;
	                            if (directPhotoInput && !(imagePreview && clearImage)) {
	                                fileInputRef.current.value = '';
	                                fileInputRef.current.click();
	                                return;
	                            }
	                            setPhotoSheetOpen(true);
	                        }}
                        sx={{
                            width: isEdit ? 52 : 48,
                            height: isEdit ? 52 : 48,
                            borderRadius: 2.5,
                            bgcolor: '#f8fafc',
                            overflow: 'hidden',
                            border: '1px solid #e5e7eb',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            cursor: fileInputRef ? 'pointer' : 'default',
                            position: 'relative',
                        }}
                    >
                        {imagePreview ? (
                            <>
                                <MedImage src={imagePreview} size={isEdit ? 52 : 48} />
                            </>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <CameraAltIcon sx={{ fontSize: 22, color: '#94a3b8' }} />
		                                <SingleLineText sx={{ mt: 0.15, fontSize: '0.62rem', color: '#94a3b8', fontWeight: 700, lineHeight: 1 }}>{localize("사진")}</SingleLineText>
                            </Box>
                        )}
                        {imageSaving && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    bgcolor: 'rgba(255, 255, 255, 0.72)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <CircularProgress size={24} sx={{ color: '#8a7000' }} />
                            </Box>
                        )}
                    </Box>

	                    <Box sx={{ flex: 1, minWidth: 0, minHeight: isEdit ? 54 : 'auto', display: 'flex', flexDirection: 'column', justifyContent: isEdit ? 'center' : 'flex-start' }}>
	                        <Box
                                role="button"
                                tabIndex={0}
                                onClick={() => setOpenSheet('name')}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        setOpenSheet('name');
                                    }
                                }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.7,
                                    width: '100%',
                                    minWidth: 0,
                                    cursor: 'pointer',
                                    borderRadius: 2,
                                    WebkitTapHighlightColor: 'transparent',
                                }}
                            >
                                <Typography sx={{
                                    fontSize: '1.08rem',
                                    fontWeight: 680,
                                    color: '#1a1a1a',
                                    lineHeight: 1.35,
                                    wordBreak: 'keep-all',
                                    overflowWrap: 'anywhere',
                                    flex: 1,
                                    minWidth: 0,
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                }}>
                                    {form.name}
                                </Typography>
	                                <IconButton
	                                    aria-label={localize("약 이름 수정")}
	                                    size="small"
	                                    onClick={(event) => {
                                            event.stopPropagation();
	                                        setOpenSheet('name');
                                    }}
                                    sx={{
                                        ...editButtonSx,
                                        width: 44,
                                        height: 44,
                                    }}
                                >
                                    <EditOutlinedIcon sx={{ fontSize: 18 }} />
                                </IconButton>
	                        </Box>
                        {smartSource && (
                            <Box
                                role="button"
                                onClick={(e) => setAiPopoverEl(e.currentTarget)}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.4,
                                    cursor: 'pointer',
                                    mt: 0.35,
                                }}
                            >
                                <AutoAwesomeIcon sx={{ fontSize: 13, color: '#15803d' }} />
                                <SingleLineText sx={{ fontSize: '0.82rem', color: '#15803d', fontWeight: 800 }}>{localize("AI가 정보를 채웠어요")}</SingleLineText>
                                <InfoOutlinedIcon sx={{ fontSize: 13, color: '#999' }} />
                            </Box>
                        )}
	                        {!isEdit && <Box sx={{ mt: 0.9, display: 'flex', flexDirection: 'column', gap: 1.1 }}>
	                            {renderSummaryRow(translationKey('알림'), isAsNeeded ? localize('정해진 시간 없음') : timesText(compactTimes))}
	                            {renderSummaryRow(translationKey('기간'), compactPeriodText)}
	                        </Box>}
                    </Box>
                    <Popover
                        disableScrollLock
                        open={!!aiPopoverEl}
                        anchorEl={aiPopoverEl}
                        onClose={() => setAiPopoverEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                        PaperProps={{ sx: { p: 1.5, maxWidth: 280, borderRadius: 3 } }}
                    >
                        <Typography sx={{ fontSize: '0.85rem', color: '#444', lineHeight: 1.6 }}>
                            {smartSource === 'api'
                                ? localize("식약처 의약품안전사용서비스 데이터를 바탕으로 자동 채워졌어요")
                                : localize("AI가 약 이름을 분석해 자동으로 채웠어요. 참고용으로만 사용해주세요")}
                        </Typography>
                    </Popover>
                </Box>

                {!isAsNeeded && (
                    <Box sx={detailCardSx}>
                        <SingleLineText sx={{ fontSize: '1.06rem', fontWeight: 700, color: '#1a1a1a', mb: 0.6, lineHeight: 1.25 }}>{localize(translationKey("알림"))}</SingleLineText>
	                        {renderDetailRow({ label: translationKey('주기'), value: freqDetail(form), onEdit: openFrequencySheet })}
                        {renderDetailRow({ label: translationKey('시간'), value: localize('하루 {v0}번', { v0: compactTimes.length }), times: compactTimes, onEdit: () => setOpenSheet('time') })}
                        {renderDetailRow({ label: translationKey('알림 방식'), value: alertMethodText, onEdit: () => setOpenSheet('alert') })}
                        {renderDetailRow({ label: translationKey('집요한 알림'), value: reminderText, onEdit: () => setOpenSheet('snooze') })}
                    </Box>
                )}

                {isAsNeeded && (
                    <Box sx={detailCardSx}>
                        <SingleLineText sx={{ fontSize: '1.06rem', fontWeight: 700, color: '#1a1a1a', mb: 0.6, lineHeight: 1.25 }}>{localize(translationKey("알림"))}</SingleLineText>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1.25, color: '#5a4a00' }}>
                            <NotificationsOffOutlinedIcon sx={{ fontSize: 22 }} />
                            <SingleLineText sx={{ fontSize: '0.98rem', fontWeight: 650 }}>{localize("필요할 때만 기록하고 알림은 보내지 않아요")}</SingleLineText>
                        </Box>
                    </Box>
                )}

                <Box sx={detailCardSx}>
                    <SingleLineText sx={{ fontSize: '1.06rem', fontWeight: 700, color: '#1a1a1a', mb: 0.6, lineHeight: 1.25 }}>
                        {currentIntakeMeta.value === 'ORAL' ? localize("복용 정보") : localize("사용 정보")}
                    </SingleLineText>
                    {renderDetailRow({
                        label: translationKey('종류'),
                        value: localize(CATEGORY_OPTIONS.find(o => o.value === form.category)?.label || '기타'),
                        onToggle: () => openDetailSheet('category'),
                    })}
                    {renderDetailRow({
                        label: translationKey('사용 방법'),
                        value: localize(currentIntakeMeta.label),
                        onToggle: () => openDetailSheet('intakeForm'),
                    })}
                    {!isAsNeeded && renderDetailRow({
                        label: currentIntakeMeta.methodLabel,
                        value: localize(METHOD_OPTIONS.find(o => o.value === form.dosageMethod)?.label || '상관없음'),
                        onToggle: () => openDetailSheet('method'),
                    })}
                    {renderDetailRow({
                        label: currentIntakeMeta.doseLabel,
                        value: `${form.dosagePerTime} ${localize(currentIntakeMeta.unit)}`,
                        onToggle: () => openDetailSheet('dosage'),
                    })}
                    {renderDetailRow({
                        label: translationKey('복용 기간'),
                        value: form.indefinite ? localize('{v0} ~ 무기한', { v0: form.startDate }) : `${form.startDate} ~ ${form.endDate || localize('미설정')}`,
                        onToggle: openPeriodSheet,
                    })}
                </Box>

                <Box sx={{ ...detailCardSx, mb: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.6 }}>
                        <SingleLineText sx={{ fontSize: '1.06rem', fontWeight: 700, color: '#1a1a1a', lineHeight: 1.25 }}>{localize("메모")}</SingleLineText>
                        <IconButton
                            aria-label={localize("메모 수정")}
                            onClick={openMemoSheet}
                            sx={editButtonSx}
                        >
                            <EditOutlinedIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Box>
                    <Typography sx={{
                        fontSize: '0.95rem',
                        color: form.memo ? '#444' : '#9ca3af',
                        fontWeight: form.memo ? 750 : 800,
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'keep-all',
                    }}>
                        {form.memo || localize("필요하면 메모를 남길 수 있어요")}
                    </Typography>
                </Box>
            </Box>

            {/* ───── BottomSheet 3종 ───── */}
            <MedicationNameEditSheet
                open={openSheet === 'name'}
                value={form.name || ''}
                onClose={() => setOpenSheet(null)}
                onSave={(nextName) => {
                    if (nextName !== form.name) patch({ name: nextName });
                }}
            />
            <BottomSheet open={openSheet === 'frequency'} onClose={() => setOpenSheet(null)} maxHeight="78vh" footer={<SheetSaveFooter onClick={commitFrequencyDraft} />}>
                <SingleLineText sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#1a1a1a', mb: 2 }}>{localize("주기 수정")}</SingleLineText>
                {frequencyDraft && (
                    <MedicationFrequencyEditor
                        value={frequencyDraft}
                        onChange={(patchValue) => setFrequencyDraft(prev => ({ ...prev, ...patchValue }))}
                    />
                )}
            </BottomSheet>
            <BottomSheet open={openSheet === 'period'} onClose={() => setOpenSheet(null)} maxHeight="78vh" footer={<SheetSaveFooter onClick={commitPeriodDraft} />}>
                <SingleLineText sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#1a1a1a', mb: 2 }}>{localize("복용 기간 수정")}</SingleLineText>
                {periodDraft && (
                    <MedicationPeriodEditor
                        value={periodDraft}
                        onChange={(patchValue) => setPeriodDraft(prev => ({ ...prev, ...patchValue }))}
                    />
                )}
            </BottomSheet>
            <BottomSheet open={detailSheetSections.includes(openSheet)} onClose={closeDetailSheet} maxHeight="78vh" footer={<SheetSaveFooter onClick={commitDetailDraft} disabled={!isValidDosage(detailDraft?.dosagePerTime)} />}>
                <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#1a1a1a', mb: 2 }}>
                    {localize(detailSheetTitleMap[openSheet]) || localize("정보 수정")}
                </Typography>
                {renderDetailSheetContent()}
            </BottomSheet>
            <BottomSheet open={openSheet === 'memo'} onClose={() => setOpenSheet(null)} maxHeight="78vh" footer={<SheetSaveFooter onClick={commitMemoDraft} />}>
                <SingleLineText sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#1a1a1a', mb: 2 }}>{localize("메모 수정")}</SingleLineText>
                <MedicationMemoEditor
                    value={memoDraft}
                    onChange={setMemoDraft}
                />
            </BottomSheet>
            <EditTimesSheet
                open={openSheet === 'time'}
                form={form}
                existingMedications={existingMedications}
                onClose={() => setOpenSheet(null)}
                onSave={(p) => patch(p)}
            />
            <EditAlertMethodSheet
                open={openSheet === 'alert'}
                form={form}
                onClose={() => setOpenSheet(null)}
                onSave={(p) => patch(p)}
            />
            <EditSnoozeSheet
                open={openSheet === 'snooze'}
                form={form}
                onClose={() => setOpenSheet(null)}
                onSave={(p) => patch(p)}
            />
            <MedicationPhotoSheet
                open={photoSheetOpen}
                onClose={() => setPhotoSheetOpen(false)}
                onSelectFile={handlePhotoInputChange}
                cameraInputRef={cameraFileInputRef}
                libraryInputRef={fileInputRef}
                onPickCamera={onPickCamera ? () => {
                    setPhotoSheetOpen(false);
                    setTimeout(onPickCamera, 180);
                } : undefined}
                onPickLibrary={onPickLibrary ? () => {
                    setPhotoSheetOpen(false);
                    setTimeout(onPickLibrary, 180);
                } : undefined}
                onDelete={imagePreview && clearImage ? clearImage : undefined}
                deleteDisabled={imageSaving}
                singlePickerMode={directPhotoInput}
                singlePickerLabel={imagePreview ? translationKey('사진 변경') : translationKey('사진 선택')}
            />
        </Box>
    );
}
